import type { Point } from "./rotation";
import type { Rect } from "./rect";

/**
 * 2D affine transform in column-vector convention:
 *   [x']   [a c e] [x]
 *   [y'] = [b d f] [y]
 *   [1 ]   [0 0 1] [1]
 *
 * This matches SVG/CSS matrix(a,b,c,d,e,f) and our SVG import path.
 * All scene-tree world/local conversion should compose through this type.
 */
export interface Affine2D {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

export const IDENTITY_AFFINE: Affine2D = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

export function affineEquals(left: Affine2D, right: Affine2D, epsilon = 1e-9): boolean {
  return Math.abs(left.a - right.a) <= epsilon
    && Math.abs(left.b - right.b) <= epsilon
    && Math.abs(left.c - right.c) <= epsilon
    && Math.abs(left.d - right.d) <= epsilon
    && Math.abs(left.e - right.e) <= epsilon
    && Math.abs(left.f - right.f) <= epsilon;
}

export function multiplyAffine(left: Affine2D, right: Affine2D): Affine2D {
  return {
    a: left.a * right.a + left.c * right.b,
    b: left.b * right.a + left.d * right.b,
    c: left.a * right.c + left.c * right.d,
    d: left.b * right.c + left.d * right.d,
    e: left.a * right.e + left.c * right.f + left.e,
    f: left.b * right.e + left.d * right.f + left.f,
  };
}

export function invertAffine(matrix: Affine2D): Affine2D | undefined {
  const determinant = matrix.a * matrix.d - matrix.b * matrix.c;
  if (!Number.isFinite(determinant) || Math.abs(determinant) < 1e-12) {
    return undefined;
  }

  const inverseDeterminant = 1 / determinant;
  return {
    a: matrix.d * inverseDeterminant,
    b: -matrix.b * inverseDeterminant,
    c: -matrix.c * inverseDeterminant,
    d: matrix.a * inverseDeterminant,
    e: (matrix.c * matrix.f - matrix.d * matrix.e) * inverseDeterminant,
    f: (matrix.b * matrix.e - matrix.a * matrix.f) * inverseDeterminant,
  };
}

export function applyAffinePoint(matrix: Affine2D, point: Point): Point {
  return {
    x: matrix.a * point.x + matrix.c * point.y + matrix.e,
    y: matrix.b * point.x + matrix.d * point.y + matrix.f,
  };
}

export function applyAffineVector(matrix: Affine2D, vector: Point): Point {
  return {
    x: matrix.a * vector.x + matrix.c * vector.y,
    y: matrix.b * vector.x + matrix.d * vector.y,
  };
}

export function affineTranslation(tx: number, ty: number): Affine2D {
  return { a: 1, b: 0, c: 0, d: 1, e: tx, f: ty };
}

export function affineScale(sx: number, sy: number, origin: Point = { x: 0, y: 0 }): Affine2D {
  return multiplyAffine(
    affineTranslation(origin.x, origin.y),
    multiplyAffine(
      { a: sx, b: 0, c: 0, d: sy, e: 0, f: 0 },
      affineTranslation(-origin.x, -origin.y),
    ),
  );
}

export function affineRotation(degrees: number, origin: Point = { x: 0, y: 0 }): Affine2D {
  if (degrees === 0) {
    return IDENTITY_AFFINE;
  }

  const radians = (degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return multiplyAffine(
    affineTranslation(origin.x, origin.y),
    multiplyAffine(
      { a: cos, b: sin, c: -sin, d: cos, e: 0, f: 0 },
      affineTranslation(-origin.x, -origin.y),
    ),
  );
}

export function affineCorners(rect: Rect): Point[] {
  return [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x, y: rect.y + rect.height },
  ];
}

export function boundsFromAffinePoints(points: Point[]): Rect {
  if (points.length === 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const left = Math.min(...points.map(point => point.x));
  const top = Math.min(...points.map(point => point.y));
  const right = Math.max(...points.map(point => point.x));
  const bottom = Math.max(...points.map(point => point.y));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

export function boundsFromAffineRect(matrix: Affine2D, rect: Rect): Rect {
  return boundsFromAffinePoints(affineCorners(rect).map(corner => applyAffinePoint(matrix, corner)));
}

/**
 * Maps bounds-local coordinates (0..width, 0..height) to document space,
 * applying rotation around a document-space origin after translating by bounds.
 */
export function affineLocalBoundsToWorld(bounds: Rect, rotation: number, rotationOrigin: Point): Affine2D {
  if (rotation === 0) {
    return affineTranslation(bounds.x, bounds.y);
  }

  const rotatedOrigin = applyAffinePoint(affineRotation(rotation, rotationOrigin), { x: bounds.x, y: bounds.y });
  const topRight = applyAffinePoint(affineRotation(rotation, rotationOrigin), {
    x: bounds.x + bounds.width,
    y: bounds.y,
  });
  const bottomLeft = applyAffinePoint(affineRotation(rotation, rotationOrigin), {
    x: bounds.x,
    y: bounds.y + bounds.height,
  });

  const xAxis = {
    x: (topRight.x - rotatedOrigin.x) / (bounds.width || 1),
    y: (topRight.y - rotatedOrigin.y) / (bounds.width || 1),
  };
  const yAxis = {
    x: (bottomLeft.x - rotatedOrigin.x) / (bounds.height || 1),
    y: (bottomLeft.y - rotatedOrigin.y) / (bounds.height || 1),
  };

  return {
    a: xAxis.x,
    b: xAxis.y,
    c: yAxis.x,
    d: yAxis.y,
    e: rotatedOrigin.x,
    f: rotatedOrigin.y,
  };
}
